import { BASE_URL } from 'src/constants/url'
import { client } from './baseService'
import {
  FileRepositoryModel,
  FileRepositoryListResponse,
  FileRepositoryListParams,
  CreateDirectoryInput,
  UpdateEntryInput,
} from 'src/models/file-repository-model'

const BASE_PATH = `${BASE_URL}/file-repository`

// ── List entries (paginated, filterable) ──────────────────────────
// GET request → companyId goes as query param
export const fetchFileRepositoryEntries = async (
  params?: FileRepositoryListParams
): Promise<FileRepositoryListResponse | undefined> => {
  try {
    const res = await client().get(BASE_PATH, { params })
    return res.data
  } catch (error) {
    return undefined
  }
}

// ── Get single entry ──────────────────────────────────────────────
export const getFileRepositoryById = async (
  id: string,
  companyId?: string
): Promise<FileRepositoryModel | undefined> => {
  try {
    const res = await client().get(`${BASE_PATH}/${id}`, {
      params: companyId ? { companyId } : undefined,
    })
    return res.data
  } catch (error) {
    return undefined
  }
}

// ── Upload file (multipart/form-data) ─────────────────────────────
// POST request → companyId goes in the body (FormData)
export const uploadFileRepository = async (
  file: File,
  parentId?: string | null,
  description?: string,
  companyId?: string
): Promise<FileRepositoryModel | false> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    if (parentId) formData.append('parentId', parentId)
    if (description) formData.append('description', description)
    if (companyId) formData.append('companyId', companyId)

    const res = await client().post(`${BASE_PATH}/upload`, formData)
    return res.data
  } catch (error) {
    return false
  }
}

// ── Create directory ──────────────────────────────────────────────
// POST request → companyId goes in the body
export const createDirectory = async (
  input: CreateDirectoryInput
): Promise<FileRepositoryModel | false> => {
  try {
    const res = await client().post(`${BASE_PATH}/directory`, input)
    return res.data
  } catch (error) {
    return false
  }
}

// ── Download file (permission-gated) ──────────────────────────────
// GET request → companyId goes as query param
export const downloadFileRepository = async (
  id: string,
  companyId?: string
): Promise<{ url: string } | undefined> => {
  try {
    const res = await client().get(`${BASE_PATH}/${id}/download`, {
      params: companyId ? { companyId } : undefined,
    })
    return res.data
  } catch (error) {
    return undefined
  }
}

// ── Update entry (rename / move / edit description) ───────────────
// PATCH request → companyId goes in the body
export const updateFileRepository = async (
  id: string,
  input: UpdateEntryInput
): Promise<FileRepositoryModel | false> => {
  try {
    const res = await client().patch(`${BASE_PATH}/${id}`, input)
    return res.data
  } catch (error) {
    return false
  }
}

// ── Count children of a directory ─────────────────────────────────
export const countDirectoryChildren = async (
  parentId: string,
  companyId?: string
): Promise<number> => {
  try {
    const res = await client().get(BASE_PATH, {
      params: {
        parentId,
        page: 1,
        limit: 1,
        ...(companyId ? { companyId } : {}),
      },
    })
    return res.data?.total ?? 0
  } catch (error) {
    return 0
  }
}

// ── Delete entry (soft delete) ────────────────────────────────────
// DELETE request → companyId goes as query param
export const deleteFileRepository = async (
  id: string,
  companyId?: string
): Promise<boolean> => {
  try {
    await client().delete(`${BASE_PATH}/${id}`, {
      params: companyId ? { companyId } : undefined,
    })
    return true
  } catch (error) {
    return false
  }
}
